import React, { useEffect, useState } from "react";
import Input from "../../FormInputs";
import { useFormik } from "formik";
import {
  franchiseOfferSchema,
  uploadOfferSchema,
} from "../../../../utils/validationSchemas";
import PrimaryButton from "../../PrimaryButton";
import DayTimeSelector from "./DayTimeSelector";
import GoogleMaps from "../../../common/GoogleMaps";
import UploadExcel from "./UploadExcel";
import {
  addFranchise,
  getLocationById,
} from "../../../../services/franchiseService";
import { useDispatch, useSelector } from "react-redux";
import PhoneFormInput from "../../PhoneInput";

const AddNewLocation = ({
  allStores,
  setLocations,
  setSelectedLocations,
  setSelectedLocationIds,
  setLocationNames,
  selectedMerchant,
  onSuccess,
  isDrawer = false
}) => {
  const dispatch = useDispatch();
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const format = "hh:mm A";

  const [fileData, setFileData] = useState();
  const [placeId, setPlaceId] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [newLocationId, setNewLocationId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    franchiseName: "",
    email: "",
    contact: "",
    location: "",
    timeSet: false,
    timeData: days?.map((d) => ({
      day: d,
      openTime: undefined,
      closeTime: undefined,
      isSelected: false,
    })),
  };
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: franchiseOfferSchema,

    onSubmit: async (values) => {
      if (allStores) return;
      setIsSubmitting(true);

      const payload =
        role === "admin"
          ? {
            merchant: selectedMerchant?._id,
            name: values?.franchiseName,
            email: values?.email,
            phone: values?.contact,
            googlePlaceId: placeId !== null ? placeId : null,
            tradingHours: timeData
              .filter((day) => day?.isSelected === true)
              .map((day) => ({
                day: day?.day?.toLowerCase(),
                openTime: day?.openTime ? day?.openTime.format(format) : null,
                closeTime: day?.closeTime
                  ? day?.closeTime.format(format)
                  : null,
              })),
          }
          : {
            name: values?.franchiseName,
            email: values?.email,
            phone: values?.contact,
            googlePlaceId: placeId !== null ? placeId : null,
            tradingHours: timeData
              .filter((day) => day?.isSelected === true)
              .map((day) => ({
                day: day?.day?.toLowerCase(),
                openTime: day?.openTime ? day?.openTime.format(format) : null,
                closeTime: day?.closeTime
                  ? day?.closeTime.format(format)
                  : null,
              })),
          };
      try {
        const addPromise = await dispatch(addFranchise(payload)).unwrap();
        if (addPromise?.success === true) {
          setNewLocationId(addPromise?.data?._id);
          setSelectedLocations((prev) => [addPromise?.data, ...prev]);
          setSelectedLocationIds((prev) => [addPromise?.data?._id, ...prev]);
          setLocationNames((prev) => [addPromise?.data?.name, ...prev]);
          resetForm();
          setPlaceId(null);
          if (onSuccess) onSuccess();
        }
      } catch (error) {
        console.error("Error creating location:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const getLocationData = async () => {
    const getPromise = await dispatch(
      getLocationById({ id: newLocationId })
    )?.unwrap();
    if (getPromise?.data) {
      setLocations((prev) => [getPromise?.data, ...prev]);
    }
  };

  useEffect(() => {
    if (newLocationId !== null && !allStores) {
      getLocationData();
    }
  }, [newLocationId, allStores]);

  const { franchiseName, email, contact, location, timeSet, timeData } =
    formik.values;

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldError,
    handleSubmit,
    setValues,
    setErrors,
    resetForm,
  } = formik;

  const handleTimeChange = (dayIndex, field, time) => {
    const updatedTimeData = [...timeData];
    updatedTimeData[dayIndex][field] = time;
    setFieldValue("timeData", updatedTimeData);
  };

  useEffect(() => {
    const allTimeSet = timeData?.every((day) => day.isSelected === false);
    setFieldValue("timeSet", allTimeSet);
  }, [timeData]);

  const handleLocationSelect = (selectedLocation) => {
    setFieldValue("location", selectedLocation?.placeName);
    setPlaceId(selectedLocation?.placeId);
    setShowMap(false);
    if (errors.location) {
      setErrors({ ...errors, location: undefined });
    }
  };

  const handleLocationClick = () => {
    setShowMap(true);
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();

    if (!location || !placeId) {
      formik.setFieldError("location", "Please provide google place id.");
      formik.setFieldTouched("location", true);
      return;
    }

    if (!contact || contact.trim() === "") {
      formik.setFieldError("contact", "Phone number is required.");
      formik.setFieldTouched("contact", true);
      return;
    }

    formik.handleSubmit(e);
  };

  return (
    <div className={`w-full ${isDrawer ? 'drawer-content-wrapper' : ''}`}>
      <div className="w-full">
        <h3 className="section-title">Store Information<span>*</span></h3>
        <Input
          type="text"
          label=""
          placeholder="Enter store name"
          width="100%"
          radius="5px"
          InputDivClass="w-[100%] mb-4"
          name="franchiseName"
          value={franchiseName}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.franchiseName}
          touched={touched.franchiseName}
        />
        <Input
          type="text"
          label=""
          placeholder="Enter email"
          width="100%"
          radius="5px"
          InputDivClass="w-[100%] mb-4"
          name="email"
          value={email}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.email}
          touched={touched.email}
        />
        <PhoneFormInput
          label={""}
          divClassName="w-full mb-6"
          name="contact"
          value={contact}
          placeholder="Enter contact number"
          handleBlur={handleBlur}
          error={errors.contact}
          touched={touched.contact}
          setFieldValue={setFieldValue}
          inputStyle={{ width: "100%", border: "none" }}
          buttonStyle={{ border: "none" }}
          buttonClass="!rounded-l-[5px] !bg-[#F0F0F0] md:!w-[13%] sm:!w-[15%] lg:!w-[10%] xl:!w-[12%] 2xl:!w-[10%]"
          containerClass="!rounded-[5px] !shadow-none"
          inputClass="lg:!h-[45px] md:!h-[45px] !rounded-[5px] !bg-[#FFFFFF] !text-[#303030] lg:!text-[14px] sm:!text-[14px] !font-[400] !outline-none !font-poppins !pl-[50px] !shadow-none"
          labelClass="hidden"
          errorClass="text-red-600 text-[10px] md:text-[12px] font-[500] ms-1 mt-1 block text-start"
        />

        <div className="mb-6 rounded-[8px] overflow-hidden border border-[#E0E0E0]">
          <GoogleMaps
            onLocationSelect={handleLocationSelect}
            onError={(err) => setFieldError("location", err)}
            searchContainerClass="p-0"
            compactMode={false}
            externalSearchValue={location}
            forceOpenSearch={false}
            options={{ mapTypeControl: false, streetViewControl: false }}
            mapContainerStyle="swoodle-map h-[320px] w-full relative"
            searchBarParentClassName="absolute z-[10] top-3 left-3 right-3"
            searchBarInputClassName="navSearchBar h-full w-[100%] ps-3 pe-10 text-[13px] font-[400] font-poppins outline-none bg-white text-[#303030] rounded-[6px] shadow-sm"
          />
        </div>
        {errors.location && touched.location && (
          <span className="text-red-600 text-[10px] md:text-[12px] font-[500] ms-1 mt-[-15px] mb-4 block text-start font-poppins">
            {errors.location}
          </span>
        )}

        <div className="time-table-card">
          <h3 className="section-title">Set Your Opening Days & Hours<span>*</span></h3>
          <DayTimeSelector
            timeData={timeData}
            setFieldValue={setFieldValue}
            format={format}
            error={errors?.timeData}
            touched={touched?.timeData}
          />
          {errors?.timeSet && touched?.timeSet ? (
            <span className="text-red-600 text-[11px] font-[500] ms-1">
              {errors?.timeSet}
            </span>
          ) : null}
        </div>
      </div>

      {isDrawer ? (
        <div className="drawer-footer">
          <span className="cancel-link" onClick={onSuccess}>Cancel</span>
          <button
            type="button"
            className="confirm-btn"
            onClick={handleCustomSubmit}
            disabled={isSubmitting || !location || !placeId}
          >
            {isSubmitting ? "Adding..." : "Add"}
          </button>
        </div>
      ) : (
        <div className="flex justify-end mt-8">
          <PrimaryButton
            className="w-[140px] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat"
            label="Submit"
            onClick={handleCustomSubmit}
            disabled={!location || !placeId}
          />
        </div>
      )}
    </div>
  );
};

export default AddNewLocation;
